// /src/overpassShadows.esm.js
import Feature from "ol/Feature.js";
import { fromLonLat } from "ol/proj.js";
import Polygon from "ol/geom/Polygon.js";

const OVERPASS_URL = "https://overpass-api.de/api/interpreter";

function kmBbox(lon, lat, km) {
  const dLat = km / 111.32;
  const dLon = km / (111.32 * Math.cos((lat * Math.PI) / 180));
  return {
    north: lat + dLat,
    south: lat - dLat,
    east: lon + dLon,
    west: lon - dLon,
  };
}

/**
 * Fetches raw building/tree OSM geometry from Overpass around (lon, lat).
 * Deliberately does no shadow math here — it's just the network call, so
 * callers can cache the result by location and reuse it across time/date
 * changes without re-fetching (the geometry itself doesn't depend on time).
 * @returns {Promise<Array<object>>} raw Overpass "elements"
 */
export function fetchOverpassElements({ lon, lat, bboxKm = 0.75 } = {}) {
  if (lon == null || lat == null) throw new Error("lon/lat benötigt");

  const bbox = kmBbox(lon, lat, bboxKm);
  const query = `
[out:json][timeout:60];
(
  way["building"](${bbox.south},${bbox.west},${bbox.north},${bbox.east});
  node["natural"="tree"](${bbox.south},${bbox.west},${bbox.north},${bbox.east});
);
out body geom;`;

  return fetch(OVERPASS_URL, {
    method: "POST",
    body: query,
    headers: { "Content-Type": "text/plain" },
  })
    .then((r) => {
      if (!r.ok) {
        // The free public Overpass API returns an HTML/XML error page
        // (not JSON) on rate limiting or timeouts, which would otherwise
        // just surface here as a confusing "Unexpected token '<'" JSON
        // parse error. Say what actually happened instead.
        const reason =
          r.status === 429
            ? "Rate-Limit erreicht (zu viele Anfragen) — später erneut versuchen"
            : r.status === 504
            ? "Zeitüberschreitung beim Overpass-Server"
            : "unerwartete Antwort";
        throw new Error(
          `Overpass-API antwortete mit ${r.status} ${r.statusText} (${reason})`
        );
      }
      return r.json();
    })
    .then((json) => json.elements || []);
}

/**
 * Turns raw Overpass elements (already fetched, possibly cached) into
 * shadow polygon Features for a given time of day. Pure/synchronous, no
 * network — cheap enough to call on every slider tick.
 */
export function buildShadowFeatures(
  elements,
  {
    lon,
    lat,
    datetime = new Date(),
    treeDefaults = { height: 12, crownDiameter: 6 },
    buildingDefaults = { height: 10, levelsToMeters: 3.0 },
  } = {}
) {
  // ---------- Helpers (vor Nutzung definiert) ----------
  function offset([x, y], [dx, dy]) {
    return [x + dx, y + dy];
  }
  function convexHull(pts) {
    if (pts.length <= 3) return pts.concat([pts[0]]);
    const p = pts.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const cross = (o, a, b) =>
      (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const lower = [];
    for (const pt of p) {
      while (lower.length >= 2 && cross(lower.at(-2), lower.at(-1), pt) <= 0)
        lower.pop();
      lower.push(pt);
    }
    const upper = [];
    for (let i = p.length - 1; i >= 0; i--) {
      const pt = p[i];
      while (upper.length >= 2 && cross(upper.at(-2), upper.at(-1), pt) <= 0)
        upper.pop();
      upper.push(pt);
    }
    upper.pop();
    lower.pop();
    return lower.concat(upper, [lower[0]]);
  }
  function shadowVector(az, elev, h) {
    const L = elev > 0 ? h / Math.tan(elev) : 10000;
    return [Math.sin(az) * L, Math.cos(az) * L];
  }
  function polygonShadow(poly, vec) {
    const ring = poly.getCoordinates()[0];
    const shifted = ring.map((c) => offset(c, vec));
    const all = ring.concat(shifted);
    const hull = convexHull(all);
    return new Polygon([hull]);
  }
  function treeShadowEllipse(center, vec, crownDiameter) {
    const [cx, cy] = center,
      [dx, dy] = vec,
      n = 32,
      r = crownDiameter / 2;
    const circ = Array.from({ length: n }, (_, i) => {
      const t = (i / n) * 2 * Math.PI;
      return [cx + r * Math.cos(t), cy + r * Math.sin(t)];
    });
    const shifted = circ.map((c) => [c[0] + dx, c[1] + dy]);
    const hull = convexHull(circ.concat(shifted));
    return new Polygon([hull]);
  }
  function featureHeight(tags, d) {
    const m = (v) =>
      v == null
        ? NaN
        : parseFloat(String(v).replace(",", ".").replace(" m", ""));
    let h = m(tags?.height);
    if (!isNaN(h)) return h;
    const lv = parseFloat(
      String(tags?.["building:levels"] || "").replace(",", ".")
    );
    return !isNaN(lv) ? lv * (d.levelsToMeters || 3) : d.height || 10;
  }
  function treeHeight(tags, d) {
    const h =
      tags?.height != null
        ? parseFloat(String(tags.height).replace(",", "."))
        : NaN;
    return isNaN(h) ? d.height || 12 : h;
  }
  function treeCrown(tags, d) {
    const c =
      tags?.["crown_diameter"] != null
        ? parseFloat(String(tags["crown_diameter"]).replace(",", "."))
        : NaN;
    if (!isNaN(c)) return c;
    const h = treeHeight(tags, d);
    return Math.min(0.6 * h, 8);
  }

  // ---------- Sonne ----------
  const sc = window.SunCalc;
  const pos = sc.getPosition(datetime, lat, lon);
  const az = (Math.PI * 2 + (Math.PI - pos.azimuth)) % (Math.PI * 2);
  const elev = pos.altitude;

  const feats = [];
  for (const el of elements) {
    if (
      el.type === "way" &&
      el.tags?.building &&
      Array.isArray(el.geometry) &&
      el.geometry.length >= 3
    ) {
      const ring = el.geometry.map((g) => fromLonLat([g.lon, g.lat]));
      const first = ring[0],
        last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first);
      const poly = new Polygon([ring]);
      const h = featureHeight(el.tags, buildingDefaults);
      if (h > 0) {
        const vec = shadowVector(az, elev, h);
        const geom = polygonShadow(poly, vec);
        const f = new Feature({
          kind: "building-shadow",
          osm_id: el.id,
          height: h,
        });
        f.setGeometry(geom);
        feats.push(f);
      }
    }
    if (
      el.type === "node" &&
      el.tags?.natural === "tree" &&
      typeof el.lon === "number" &&
      typeof el.lat === "number"
    ) {
      const xy = fromLonLat([el.lon, el.lat]);
      const th = treeHeight(el.tags, treeDefaults);
      const cd = treeCrown(el.tags, treeDefaults);
      const vec = shadowVector(az, elev, th);
      const geom = treeShadowEllipse(xy, vec, cd);
      const f = new Feature({
        kind: "tree-shadow",
        osm_id: el.id,
        height: th,
        crown_diameter: cd,
      });
      f.setGeometry(geom);
      feats.push(f);
    }
  }
  return feats;
}
